import { IVideoPim, IVideoType } from '@components/typescript';
import getCurrentDevice from '@utils/getCurrentDevice';
import isVideoUrl from '@utils/isVideoUrl';
import { TResponsiveType } from '@features/responsive/typescript';
type ResponsiveDevice = 'mobile' | 'tablet' | 'desktop';
const DEVICE_FALLBACK_ORDER: Record<ResponsiveDevice, ResponsiveDevice[]> = {
  mobile: ['mobile', 'tablet', 'desktop'],
  tablet: ['tablet', 'desktop', 'mobile'],
  desktop: ['desktop', 'tablet', 'mobile']
};
const YOUTUBE_ID_REGEX = /^[\w-]{11}$/;
const YOUTUBE_URL_REGEX = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
const toYouTubeEmbedUrl = (urlOrId: string): string | null => {
  if (YOUTUBE_ID_REGEX.test(urlOrId)) {
    return `https://www.youtube.com/embed/${urlOrId}`;
  }
  if (!urlOrId.includes('youtube') && !urlOrId.includes('youtu.be')) {
    return null;
  }
  const match = urlOrId.match(YOUTUBE_URL_REGEX);
  if (match && match[2].length === 11) {
    return `https://www.youtube.com/embed/${match[2]}`;
  }
  return null;
};
const extractVideoUrl = (videoValue: string | IVideoPim): string => {
  if (typeof videoValue === 'string') {
    const youtubeEmbed = toYouTubeEmbedUrl(videoValue);
    if (youtubeEmbed) {
      return youtubeEmbed;
    }
    return isVideoUrl(videoValue) ? videoValue : '';
  }
  if (videoValue.type === 'youtube') {
    return toYouTubeEmbedUrl(videoValue.data) || `https://www.youtube.com/embed/${videoValue.data}`;
  }
  if (videoValue.data) {
    const youtubeEmbed = toYouTubeEmbedUrl(videoValue.data);
    if (youtubeEmbed) {
      return youtubeEmbed;
    }
    if (isVideoUrl(videoValue.data)) {
      return videoValue.data;
    }
  }
  return '';
};
const getResponsiveVideoUrl = (video: Partial<Record<ResponsiveDevice, string | IVideoPim>>, currentDevice: ResponsiveDevice): string => {
  for (const device of DEVICE_FALLBACK_ORDER[currentDevice]) {
    const videoValue = video[device];
    if (!videoValue) {
      continue;
    }
    const url = extractVideoUrl(videoValue);
    if (url) {
      return url;
    }
  }
  return '';
};
const getVideoUrl = (video: IVideoType | string, breakpoint: TResponsiveType | undefined) => {
  const currentDevice = getCurrentDevice(breakpoint) as ResponsiveDevice;
  if (typeof video === 'string') {
    const youtubeEmbed = toYouTubeEmbedUrl(video);
    if (youtubeEmbed) {
      return youtubeEmbed;
    }
    if (video.includes('http')) {
      return isVideoUrl(video) ? video : '';
    }
    if (isVideoUrl(video)) {
      // Ścieżki względne do plików wideo (np. /videos/xxx.mp4) - zwracamy bez zmian, przeglądarka je rozwiąże
      return video;
    }
    // Traktuj jako ID YouTube
    return `https://www.youtube.com/embed/${video}`;
  }
  return getResponsiveVideoUrl(video, currentDevice);
};
export default getVideoUrl;